import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useBilling } from '../contexts/BillingContext';
import { CURRENCY_SYMBOLS } from '../config/stripeConfig';
import { getProMonthlyEmailLimit } from '../config/proEmailLimits';
import {
  PRO_MONTHLY_PRICING_TIERS,
  getProAnnualPrice,
  getProEffectiveMonthlyDisplayPrice,
  BUSINESS_MONTHLY_PRICE,
  BUSINESS_ANNUAL_PRICE,
} from '../config/proPlanPricing';
import { detectUserCountry, getCurrencyByCountry, convertCurrency, formatCurrency } from '../utils/currencyDetection';
import { usePlanPricing } from '../hooks/usePlanPricing';
import { createDodoCheckoutSession } from '../services/dodoPaymentService';
import { userService } from '../services/firestore';
import { buildContactTierOptions, subscribersToSelectValue } from '../config/contactTierOptions';
import { PLAN_FEATURES } from '../config/planFeatures';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PlanData {
  plan: 'free' | 'pro' | 'business' | 'enterprise';
  subscribers: number;
  emails: number;
  currency: string;
  billingPeriod: 'monthly' | 'annual';
}

/** Pro card: same idea as Business except contacts, sends, workspaces — no priority support line. */
const PRICING_CARD_PRO_FEATURES: readonly string[] = [
  'Automation & segmentation',
  'No Scubamail Branding',
  'Premium templates',
  'API, webhooks & optional dedicated IP',
];

/** Business card: premium template and priority support as separate bullets. */
const PRICING_CARD_BUSINESS_FEATURES: readonly string[] = [
  'Automation & segmentation',
  'No Scubamail Branding',
  'Premium template',
  'Priority Support',
  'API, webhooks & optional dedicated IP',
];

/** Starter: same structure as paid, but calls out Scubamail branding instead of white-label. */
const PRICING_CARD_STARTER_FEATURES: readonly string[] = [
  'Email builder & templates',
  'Scubamail branding on emails',
  'Email analytics',
  'API & webhooks',
];

/** Numeric limits for the comparison table (from plan config). */
function comparisonFmtLimit(n: number): string {
  if (n === Infinity || n === Number.POSITIVE_INFINITY || !Number.isFinite(n)) return 'Unlimited';
  return n.toLocaleString('en-US');
}

function comparisonYn(v: boolean): '✓' | '—' {
  return v ? '✓' : '—';
}

function comparisonFmtStorage(bytes: number): string {
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${Number.isInteger(gb) ? gb : parseFloat(gb.toFixed(1))} GB`;
  const mb = bytes / (1024 * 1024);
  return `${Math.max(1, Math.round(mb))} MB`;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showError } = useNotification();
  const { initiateShopifyBilling, preferShopifyBilling } = useBilling();
  const {
    proPricing,
    proAnnualPricing,
    businessPricing,
    isLoading: isLoadingPricing,
  } = usePlanPricing();

  const MAX_SUBSCRIBERS = 100000;
  const MIN_SUBSCRIBERS = 1000;

  /** Same tier caps as Pro pricing / Dodo product map — built from live `proPricing` when loaded. */
  const contactRanges = useMemo(
    () => buildContactTierOptions(proPricing.length > 0 ? proPricing : []),
    [proPricing]
  );


  const [formData, setFormData] = useState<PlanData>({
    plan: 'pro',
    subscribers: 10000,
    emails: 12000,
    currency: 'usd',
    billingPeriod: 'annual'
  });

  /** Which plan row is starting Dodo redirect (for button label / disable). */
  const [dodoRedirectPlan, setDodoRedirectPlan] = useState<'pro' | 'business' | null>(null);
  const [expandedFeatures, setExpandedFeatures] = useState<Record<string, boolean>>({ Limits: true, Email: true });
  const [isClosing, setIsClosing] = useState(false);

  // Handle closing logic with animation
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      // Reset isClosing after calling onClose (though component might unmount)
      // This is a safety measure in case the parent doesn't unmount immediately
      setIsClosing(false);
    }, 200); // 200ms matches the CSS animation duration
  };

  // State to store detected user country for regional pricing

  // State to store detected user country for regional pricing
  // Persist in sessionStorage to avoid re-detection every time modal opens
  const [userCountry, setUserCountry] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem('detectedCountry');
    } catch {
      return null;
    }
  });
  const [isDetectingCountry, setIsDetectingCountry] = useState(!userCountry);

  useEffect(() => {
    setFormData(prev => ({ ...prev, currency: 'usd' }));
    setUserCountry('United States');
    setIsDetectingCountry(false);
  }, [isOpen]);

  /** Snap subscriber count to a real tier cap once Pro tiers load (aligns with dropdown values and Dodo). */
  useEffect(() => {
    if (!isOpen || proPricing.length === 0) return;
    setFormData((prev) => {
      const v = subscribersToSelectValue(prev.subscribers, proPricing);
      const next = v === 100001 ? 100000 : v;
      if (next === prev.subscribers) return prev;
      return { ...prev, subscribers: next };
    });
  }, [isOpen, proPricing]);

  // Force USD always
  useEffect(() => {
    if (formData.currency !== 'usd') {
      setFormData(prev => ({ ...prev, currency: 'usd' }));
    }
  }, [formData.currency]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Calculate price based on subscriber count (synchronous version using cached pricing)
  const calculatePlanPrice = (plan: 'pro' | 'business', subscribers: number): number => {
    // Business is flat-rate — ignore subscriber count
    if (plan === 'business') {
      const currencyForConversion = userCountry
        ? getCurrencyByCountry(userCountry).toLowerCase()
        : 'usd';
      return convertCurrency(BUSINESS_MONTHLY_PRICE, currencyForConversion, userCountry);
    }

    const tiers = proPricing.length > 0 ? proPricing : PRO_MONTHLY_PRICING_TIERS;

    let usdPrice: number;
    if (subscribers < tiers[0].subscribers) {
      usdPrice = tiers[0].price;
    } else {
      const tier = tiers.find(tier => subscribers <= tier.subscribers) || tiers[tiers.length - 1];
      usdPrice = tier.price;
    }
    // Use detected currency for conversion (with regional pricing support)
    // Prioritize userCountry detection, then formData.currency
    const currencyForConversion = userCountry
      ? getCurrencyByCountry(userCountry).toLowerCase()
      : (formData.currency || 'usd');

    // Pass userCountry for regional pricing support (e.g., India gets 60% of USD price)
    const convertedPrice = convertCurrency(usdPrice, currencyForConversion, userCountry);
    return convertedPrice;
  };

  const handleSubscribersChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value) || 0;
    setFormData(prev => {
      const subscribers = value === 100001 ? 100000 : Math.max(MIN_SUBSCRIBERS, Math.min(MAX_SUBSCRIBERS, value));
      const newPlan = prev.plan === 'free' && subscribers > 1000 ? 'pro' : prev.plan;
      return {
        ...prev,
        subscribers,
        plan: newPlan
      };
    });
  };

  const handleBillingPeriodChange = (period: 'monthly' | 'annual') => {
    setFormData(prev => ({
      ...prev,
      billingPeriod: period
    }));
  };

  const formatSubscribers = (count: number): string => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  /** Feature matrix sourced from `planFeatures.ts` — no speculative channels (SMS/push/ads). */
  const planComparisonCategories = useMemo(() => {
    const S = PLAN_FEATURES.free;
    const P = PLAN_FEATURES.pro;
    const B = PLAN_FEATURES.business;
    const tierLabel = formatSubscribers(formData.subscribers);
    const proSendCap = getProMonthlyEmailLimit(formData.subscribers);

    const reportHist = (days: number) =>
      days === Infinity || !Number.isFinite(days) ? 'Unlimited' : `${days} days`;

    return [
      {
        name: 'Limits',
        subFeatures: [
          { name: 'Max contacts', starter: comparisonFmtLimit(S.maxSubscribers), pro: tierLabel, business: tierLabel },
          { name: 'Workspaces', starter: comparisonFmtLimit(S.maxWorkspaces), pro: comparisonFmtLimit(P.maxWorkspaces), business: 'Unlimited' },
          { name: 'Email sends per month', starter: '12,000*', pro: `${formatSubscribers(proSendCap)}*`, business: 'Unlimited*' },
          { name: 'Campaigns (max)', starter: comparisonFmtLimit(S.maxCampaigns), pro: comparisonFmtLimit(P.maxCampaigns), business: comparisonFmtLimit(B.maxCampaigns) },
          { name: 'Active campaigns (max)', starter: comparisonFmtLimit(S.maxActiveCampaigns), pro: comparisonFmtLimit(P.maxActiveCampaigns), business: comparisonFmtLimit(B.maxActiveCampaigns) },
          { name: 'Automations (max)', starter: comparisonFmtLimit(S.maxAutomations), pro: comparisonFmtLimit(P.maxAutomations), business: comparisonFmtLimit(B.maxAutomations) },
          { name: 'Active automations (max)', starter: comparisonFmtLimit(S.maxActiveAutomations), pro: comparisonFmtLimit(P.maxActiveAutomations), business: comparisonFmtLimit(B.maxActiveAutomations) },
          { name: 'Signup forms (max)', starter: comparisonFmtLimit(S.maxSignupForms), pro: comparisonFmtLimit(P.maxSignupForms), business: comparisonFmtLimit(B.maxSignupForms) },
          { name: 'Landing pages (max)', starter: comparisonFmtLimit(S.maxLandingPages), pro: comparisonFmtLimit(P.maxLandingPages), business: comparisonFmtLimit(B.maxLandingPages) },
          { name: 'Storage', starter: comparisonFmtStorage(S.maxStorage), pro: comparisonFmtStorage(P.maxStorage), business: comparisonFmtStorage(B.maxStorage) },
        ],
      },
      {
        name: 'Email',
        subFeatures: [
          { name: 'Email scheduling', starter: comparisonYn(S.features.emailScheduling), pro: comparisonYn(P.features.emailScheduling), business: comparisonYn(B.features.emailScheduling) },
          { name: 'A/B testing (campaigns)', starter: comparisonYn(S.features.abTesting), pro: comparisonYn(P.features.abTesting), business: comparisonYn(B.features.abTesting) },
        ],
      },
      {
        name: 'Automation',
        subFeatures: [
          { name: 'Advanced automations', starter: comparisonYn(S.features.advancedAutomations), pro: comparisonYn(P.features.advancedAutomations), business: comparisonYn(B.features.advancedAutomations) },
          { name: 'Trigger filters', starter: comparisonYn(S.features.triggerFilters), pro: comparisonYn(P.features.triggerFilters), business: comparisonYn(B.features.triggerFilters) },
          { name: 'Goal tracking', starter: comparisonYn(S.features.goalTracking), pro: comparisonYn(P.features.goalTracking), business: comparisonYn(B.features.goalTracking) },
          { name: 'Automation analytics', starter: comparisonYn(S.features.automationAnalytics), pro: comparisonYn(P.features.automationAnalytics), business: comparisonYn(B.features.automationAnalytics) },
        ],
      },
      {
        name: 'Brand & domain',
        subFeatures: [
          { name: 'Custom branding', starter: comparisonYn(S.features.customBranding), pro: comparisonYn(P.features.customBranding), business: comparisonYn(B.features.customBranding) },
          { name: 'Custom domain', starter: comparisonYn(S.features.customDomain), pro: comparisonYn(P.features.customDomain), business: comparisonYn(B.features.customDomain) },
        ],
      },
      {
        name: 'Integrations',
        subFeatures: [
          { name: 'API access', starter: comparisonYn(S.features.apiAccess), pro: comparisonYn(P.features.apiAccess), business: comparisonYn(B.features.apiAccess) },
          { name: 'Webhooks', starter: comparisonYn(S.features.webhooks), pro: comparisonYn(P.features.webhooks), business: comparisonYn(B.features.webhooks) },
          { name: 'Custom integrations', starter: comparisonYn(S.features.customIntegrations), pro: comparisonYn(P.features.customIntegrations), business: comparisonYn(B.features.customIntegrations) },
          { name: 'Shopify integration', starter: comparisonYn(S.features.shopifyIntegration), pro: comparisonYn(P.features.shopifyIntegration), business: comparisonYn(B.features.shopifyIntegration) },
          { name: 'WooCommerce integration', starter: comparisonYn(S.features.woocommerceIntegration), pro: comparisonYn(P.features.woocommerceIntegration), business: comparisonYn(B.features.woocommerceIntegration) },
        ],
      },
      {
        name: 'Support',
        subFeatures: [
          { name: 'Email support', starter: comparisonYn(S.features.emailSupport), pro: comparisonYn(P.features.emailSupport), business: comparisonYn(B.features.emailSupport) },
          { name: 'Priority support', starter: comparisonYn(S.features.prioritySupport), pro: comparisonYn(P.features.prioritySupport), business: comparisonYn(B.features.prioritySupport) },
        ],
      },
      {
        name: 'Reporting',
        subFeatures: [
          { name: 'Advanced reports', starter: comparisonYn(S.features.advancedReports), pro: comparisonYn(P.features.advancedReports), business: comparisonYn(B.features.advancedReports) },
          { name: 'Data export', starter: comparisonYn(S.features.dataExport), pro: comparisonYn(P.features.dataExport), business: comparisonYn(B.features.dataExport) },
          { name: 'Custom reports', starter: comparisonYn(S.features.customReports), pro: comparisonYn(P.features.customReports), business: comparisonYn(B.features.customReports) },
          { name: 'Report history', starter: reportHist(S.features.reportHistory), pro: reportHist(P.features.reportHistory), business: reportHist(B.features.reportHistory) },
        ],
      },
    ];
  }, [formData.subscribers]);

  const calculateEffectiveMonthlyPrice = (
    plan: 'pro' | 'business',
    subscribers: number,
    billingPeriod: 'monthly' | 'annual'
  ): number => {
    if (plan === 'pro') {
      const monthlyTiers = proPricing.length > 0 ? proPricing : PRO_MONTHLY_PRICING_TIERS;
      const annualTiers = proAnnualPricing.length > 0 ? proAnnualPricing : undefined;
      const usd = getProEffectiveMonthlyDisplayPrice(
        subscribers,
        billingPeriod,
        monthlyTiers,
        annualTiers
      );
      const currencyForConversion = userCountry
        ? getCurrencyByCountry(userCountry).toLowerCase()
        : (formData.currency || 'usd');
      return convertCurrency(usd, currencyForConversion, userCountry);
    }
    // Business is flat-rate
    const currencyForConversion = userCountry
      ? getCurrencyByCountry(userCountry).toLowerCase()
      : (formData.currency || 'usd');
    const usd = billingPeriod === 'annual' ? BUSINESS_ANNUAL_PRICE / 12 : BUSINESS_MONTHLY_PRICE;
    return convertCurrency(usd, currencyForConversion, userCountry);
  };

  const getProAnnualTotalDisplay = (subscribers: number): number => {
    const annualTiers = proAnnualPricing.length > 0 ? proAnnualPricing : undefined;
    const usd = getProAnnualPrice(subscribers, annualTiers);
    const currencyForConversion = userCountry
      ? getCurrencyByCountry(userCountry).toLowerCase()
      : (formData.currency || 'usd');
    return convertCurrency(usd, currencyForConversion, userCountry);
  };

  const beginPaidCheckout = async (plan: 'pro' | 'business') => {
    if (!currentUser) {
      showError('You must be logged in to select a plan');
      navigate('/login');
      return;
    }
    // Only Shopify-hosted billing when embedded (?shop=) or workspace is Shopify-linked.
    // Do not use sessionStorage alone — a stale shop value blocks Dodo with no redirect.
    if (preferShopifyBilling) {
      try {
        await initiateShopifyBilling(plan, formData.subscribers, formData.billingPeriod);
      } catch (error) {
        console.error('Error initiating Shopify billing:', error);
        showError('Failed to initiate Shopify billing.');
      }
      return;
    }

    // Web app: always open Dodo hosted checkout from this modal (no in-app checkout step).
    setDodoRedirectPlan(plan);
    try {
      let customerEmail = currentUser.email || '';
      let customerName = (currentUser.displayName && currentUser.displayName.trim()) || '';
      try {
        const profile = await userService.getUserProfile(currentUser.uid);
        if (profile) {
          if (profile.email) customerEmail = profile.email;
          const fromProfile = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
          if (fromProfile) customerName = fromProfile;
        }
      } catch {
        /* use auth fallbacks */
      }
      if (!customerName) {
        customerName = customerEmail.split('@')[0] || 'Customer';
      }
      const { checkoutUrl } = await createDodoCheckoutSession({
        plan,
        subscribers: plan === 'business' ? 0 : formData.subscribers, // Business is flat-rate
        billingPeriod: formData.billingPeriod,
        customerEmail,
        customerName,
        returnOrigin: typeof window !== 'undefined' ? window.location.origin : '',
      });
      window.location.assign(checkoutUrl);
    } catch (e) {
      console.error('[UpgradeModal] Dodo checkout', e);
      showError(e instanceof Error ? e.message : 'Could not start checkout');
      setDodoRedirectPlan(null);
    }
  };

  const handleProPlan = async () => {
    await beginPaidCheckout('pro');
  };

  const handleBusinessPlan = async () => {
    await beginPaidCheckout('business');
  };

  const handleStartFreePlan = () => {
    if (formData.subscribers > 1000) return;
    setFormData((prev) => ({ ...prev, plan: 'free' }));
    onClose();
    navigate('/pricing');
  };

  const paidCheckoutBusy = dodoRedirectPlan !== null;

  // Use detected currency for display (with regional pricing)
  // ALWAYS prioritize userCountry detection - this is the source of truth
  // Only fallback to formData.currency if userCountry is truly null
  // This ensures currency doesn't flip back to USD after detection completes
  const detectedCurrency = userCountry
    ? getCurrencyByCountry(userCountry)
    : (formData.currency && formData.currency !== 'usd' ? formData.currency.toUpperCase() : 'USD');

  // Force displayCurrency to always use detectedCurrency (never stale formData.currency)
  const displayCurrency = detectedCurrency.toLowerCase();

  const currencySymbol = CURRENCY_SYMBOLS[displayCurrency as keyof typeof CURRENCY_SYMBOLS] || '$';

  // Calculate prices using the detected currency
  // Prices will automatically use regional pricing if userCountry is set
  const proPrice = calculateEffectiveMonthlyPrice('pro', formData.subscribers, formData.billingPeriod);
  const businessPrice = calculateEffectiveMonthlyPrice('business', formData.subscribers, formData.billingPeriod);

  if (!isOpen) return null;

  return createPortal(
    <>
      <style>{`
        .workspace-tooltip {
          position: relative;
          cursor: help;
          border-bottom: 1px dotted currentColor;
        }
        .workspace-tooltip::before {
          content: "A workspace is a separate environment to manage different brands or projects with their own contacts and campaigns.";
          position: absolute;
          bottom: 125%;
          left: 50%;
          transform: translateX(-50%);
          width: 200px;
          padding: 8px;
          background: #1e293b;
          color: white;
          font-size: 10px;
          font-weight: 500;
          border-radius: 6px;
          visibility: hidden;
          opacity: 0;
          transition: opacity 0.2s;
          z-index: 50;
          text-align: center;
          line-height: 1.4;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        }
        .workspace-tooltip:hover::before {
          visibility: visible;
          opacity: 1;
        }
        .upgrade-list-size-tooltip {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          cursor: help;
          color: #9ca3af;
        }
        .upgrade-list-size-tooltip::before {
          content: attr(data-tip);
          position: absolute;
          bottom: 125%;
          left: 50%;
          transform: translateX(-50%);
          width: min(280px, 85vw);
          padding: 10px 12px;
          background: #1e293b;
          color: white;
          font-size: 11px;
          font-weight: 500;
          border-radius: 8px;
          visibility: hidden;
          opacity: 0;
          transition: opacity 0.2s;
          z-index: 50;
          text-align: left;
          line-height: 1.45;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.2);
          pointer-events: none;
        }
        .upgrade-list-size-tooltip:hover::before {
          visibility: visible;
          opacity: 1;
        }
      `}</style>
      {/* Full-screen overlay */}
      <div className={`fixed inset-0 z-[9999] bg-white overflow-y-auto ${isClosing ? 'animate-modal-scale-out' : 'animate-modal-scale-in'}`}>
        {/* Floating Close Button */}
        <button
          onClick={handleClose}
          className="fixed top-6 right-6 z-20 p-2 bg-white hover:bg-gray-100 rounded-lg shadow-lg transition-colors duration-200"
          aria-label="Close modal"
        >
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Main Content */}
        <div className="relative min-h-screen bg-neutral-900">
          {paidCheckoutBusy && (
            <div
              className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 px-4"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <div className="w-full max-w-md rounded-2xl border border-neutral-700 bg-neutral-900 px-8 py-8 text-center shadow-xl">
                <div
                  className="mx-auto mb-5 h-10 w-10 rounded-full border-2 border-neutral-600 border-t-[#C6FE1E] animate-spin"
                  aria-hidden
                />
                <p className="text-lg font-medium text-white">Redirecting to secure checkout</p>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                  Hang tight—we&apos;re opening our payment partner&apos;s page so you can finish your subscription.
                </p>
              </div>
            </div>
          )}
          {/* Pricing Section */}
          <section className="pt-16 pb-8 overflow-hidden">
            <div className="container px-4 mx-auto">
              {/* List size + free plan CTA (matches marketing layout: heading, row, reassurance) */}
              <div className="mb-10 max-w-2xl mx-auto mt-2 px-2">
                <div className="flex items-start justify-center gap-2 mb-4">
                  <h2 className="text-center text-lg sm:text-xl font-semibold text-white leading-snug">
                    How many people are you sending to?
                  </h2>
                  <span
                    className="upgrade-list-size-tooltip mt-0.5"
                    data-tip="Pick the list size you expect to email. Pro and Business prices change with the tier you choose. Starter stays free up to 1,000 contacts."
                    aria-label="About list size"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </span>
                </div>
                <div className="flex justify-center">
                  <div className="relative w-full max-w-sm">
                    <label htmlFor="upgrade-modal-list-size" className="sr-only">
                      List size
                    </label>
                    <select
                      id="upgrade-modal-list-size"
                      value={subscribersToSelectValue(formData.subscribers, proPricing)}
                      onChange={handleSubscribersChange}
                      className="w-full bg-white text-neutral-900 px-4 py-3 rounded-lg border-2 border-neutral-900 hover:border-[#C6FE1E] focus:border-[#C6FE1E] focus:outline-none focus:ring-2 focus:ring-[#C6FE1E]/30 transition-all duration-200 cursor-pointer appearance-none text-sm font-medium"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23171717' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        backgroundSize: '16px',
                        paddingRight: '40px',
                      }}
                    >
                      {contactRanges.map((range) => (
                        <option key={range.value} value={range.value} className="bg-white text-neutral-900">
                          {range.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-3 text-sm text-gray-400">
                  <span>No credit card | Cancel any time</span>
                  <span
                    className="upgrade-list-size-tooltip"
                    data-tip="Starter is free for up to 1,000 contacts. Paid plans bill through checkout when you choose Pro or Business. You can change or cancel later from your account."
                    aria-label="About billing"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </span>
                </div>
              </div>

              {/* Paid plans: shared billing cycle (applies to Pro & Business) */}
              <div className="flex flex-col items-center gap-1.5 mb-6" role="group" aria-label="Billing cycle">
                <div className="inline-flex rounded-full border border-white/12 p-0.5 bg-neutral-800/90 shadow-inner">
                  <button
                    type="button"
                    onClick={() => handleBillingPeriodChange('monthly')}
                    className={`px-3 sm:px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                      formData.billingPeriod === 'monthly'
                        ? 'bg-[#C6FE1E] text-neutral-900 shadow-sm'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBillingPeriodChange('annual')}
                    className={`px-3 sm:px-4 py-1.5 rounded-full text-xs font-medium transition-all inline-flex items-center gap-1 ${
                      formData.billingPeriod === 'annual'
                        ? 'bg-[#C6FE1E] text-neutral-900 shadow-sm'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Annually
                    <span
                      className={`text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded leading-none ${
                        formData.billingPeriod === 'annual'
                          ? 'bg-neutral-900 text-[#C6FE1E] border border-neutral-800 shadow-sm'
                          : 'text-gray-400 border border-white/15 bg-white/5'
                      }`}
                    >
                      Save 10%
                    </span>
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap -m-4">
                {/* Starter Plan */}
                <div className="w-full md:w-1/2 lg:w-1/3 p-4">
                  <div className={`relative px-8 pt-8 pb-8 h-full bg-neutral-800 border-2 ${formData.plan === 'free' ? 'border-[#C6FE1E]' : 'border-gray-600'
                    } overflow-hidden rounded-3xl hover:border-[#d4ff4a] transition-all duration-200 flex flex-col`}>
                    <p className="mb-2 text-lg text-white font-light">Starter</p>
                    <p className="mb-4 text-gray-300">Essential tools to build your audience and send professional newsletters with ease.</p>
                    <p className="mb-4 text-white font-medium text-5xl">
                      {currencySymbol}0
                      <span className="text-base font-medium text-gray-400">/ month</span>
                    </p>
                    <p className="mb-3 text-xs text-gray-400 font-light uppercase">What you get</p>
                    <ul className="mb-4 space-y-2">
                      <li>
                        <p className="text-white text-sm">Up to 1,000 contacts</p>
                      </li>
                      <li>
                        <p className="text-white text-sm">12,000 sends/month*</p>
                      </li>
                      <li>
                        <p className="text-white text-sm">
                          1 <span className="workspace-tooltip">workspace</span>
                        </p>
                      </li>
                      {PRICING_CARD_STARTER_FEATURES.map((label) => (
                        <li key={label}>
                          <p className="text-white text-sm">{label}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto pt-4">
                      <button
                        type="button"
                        onClick={handleStartFreePlan}
                        disabled={formData.subscribers > 1000}
                        className={`relative z-10 block w-full px-14 py-4 text-center font-medium tracking-wide border-2 ${formData.subscribers > 1000
                          ? 'border-gray-500 bg-neutral-700 text-gray-400 cursor-not-allowed'
                          : formData.plan === 'free'
                            ? 'border-[#C6FE1E] bg-[#C6FE1E] text-neutral-900'
                            : 'border-gray-500 bg-neutral-700 hover:bg-neutral-600 text-white'
                          } focus:ring-4 focus:ring-gray-500 focus:ring-opacity-40 rounded-full transition duration-300`}
                      >
                        {formData.subscribers > 1000 ? 'Not available' : 'Start your free plan'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Pro Plan */}
                <div className="w-full md:w-1/2 lg:w-1/3 p-4">
                  <div className={`relative px-8 pt-8 pb-8 h-full bg-neutral-800 border-2 ${formData.plan === 'pro' ? 'border-[#C6FE1E]' : 'border-[#C6FE1E]'
                    } overflow-hidden rounded-3xl hover:border-[#d4ff4a] transition-all duration-200 flex flex-col`}>
                    <div className="absolute top-4 right-4 bg-[#C6FE1E] text-neutral-900 px-3 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </div>
                    <p className="mb-2 text-lg text-white font-light">Pro</p>
                    <p className="mb-4 text-gray-300">Maximize engagement with advanced automation, deep analytics, and white-labeled emails.</p>
                    <p className="mb-4 text-white font-medium text-5xl">
                      {formatCurrency(proPrice, displayCurrency)}
                      <span className="text-base font-medium text-gray-400">/ month</span>
                    </p>
                    {formData.billingPeriod === 'annual' && (
                      <div className="mb-4">
                        <p className="text-xl sm:text-2xl font-semibold text-white tracking-tight">
                          {formatCurrency(getProAnnualTotalDisplay(formData.subscribers), displayCurrency)}
                          <span className="text-sm sm:text-base font-medium text-gray-400"> / year</span>
                        </p>
                        <p className="mt-1 text-sm text-gray-400">
                          Billed yearly for up to {formatSubscribers(formData.subscribers)} subscribers
                        </p>
                      </div>
                    )}
                    {formData.billingPeriod === 'monthly' && (
                      <p className="mb-4 text-sm text-gray-400">
                        {formatCurrency(calculatePlanPrice('pro', formData.subscribers), displayCurrency)} / month for up to {formatSubscribers(formData.subscribers)} subscribers
                      </p>
                    )}

                    <p className="mb-3 text-xs text-gray-400 font-light uppercase">What you get</p>
                    <ul className="mb-4 space-y-2">
                      <li>
                        <p className="text-white text-sm">Up to {formatSubscribers(formData.subscribers)} contacts</p>
                      </li>
                      <li>
                        <p className="text-white text-sm">
                          {formatSubscribers(getProMonthlyEmailLimit(formData.subscribers))} sends/month*
                        </p>
                      </li>
                      <li>
                        <p className="text-white text-sm">
                          3 <span className="workspace-tooltip">workspaces</span>
                        </p>
                      </li>
                      {PRICING_CARD_PRO_FEATURES.map((label) => (
                        <li key={label}>
                          <p className="text-white text-sm">{label}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto pt-4">
                      <button
                        type="button"
                        disabled={paidCheckoutBusy}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, plan: 'pro' }));
                          handleProPlan();
                        }}
                        className={`relative z-10 block w-full px-14 py-4 text-center font-medium tracking-wide border-2 border-[#C6FE1E] bg-white text-neutral-900 hover:bg-neutral-100 focus:ring-4 focus:ring-[#C6FE1E] focus:ring-opacity-40 rounded-full transition duration-300 ${paidCheckoutBusy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {dodoRedirectPlan === 'pro'
                          ? 'Opening checkout…'
                          : 'Continue with pro'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Business Plan */}
                <div className="w-full lg:w-1/3 p-4">
                  <div className={`relative px-8 pt-8 pb-8 h-full bg-neutral-800 border-2 ${formData.plan === 'business' ? 'border-[#C6FE1E]' : 'border-gray-600'
                    } overflow-hidden rounded-3xl hover:border-[#d4ff4a] transition-all duration-200 flex flex-col`}>
                    <p className="mb-2 text-lg text-white font-light">Business</p>
                    <p className="mb-4 text-gray-300">The ultimate scaling engine for high-volume teams requiring 24/7 priority support.</p>
                    <p className="mb-4 text-white font-medium text-5xl">
                      {formatCurrency(businessPrice, displayCurrency)}
                      <span className="text-base font-medium text-gray-400">/ month</span>
                    </p>
                    {formData.billingPeriod === 'annual' && (
                      <div className="mb-4">
                        <p className="text-xl sm:text-2xl font-semibold text-white tracking-tight">
                          {formatCurrency(convertCurrency(BUSINESS_ANNUAL_PRICE, displayCurrency === 'usd' ? 'usd' : displayCurrency, userCountry), displayCurrency)}
                          <span className="text-sm sm:text-base font-medium text-gray-400"> / year</span>
                        </p>
                        <p className="mt-1 text-sm text-gray-400">
                          Billed yearly — unlimited contacts &amp; emails
                        </p>
                      </div>
                    )}
                    {formData.billingPeriod === 'monthly' && (
                      <p className="mb-4 text-sm text-gray-400">
                        {formatCurrency(BUSINESS_MONTHLY_PRICE, displayCurrency)} / month — unlimited contacts &amp; emails
                      </p>
                    )}

                    <p className="mb-3 text-xs text-gray-400 font-light uppercase">What you get</p>
                    <ul className="mb-4 space-y-2">
                      <li>
                        <p className="text-white text-sm">Unlimited contacts</p>
                      </li>
                      <li>
                        <p className="text-white text-sm">Unlimited sends/month*</p>
                      </li>
                      <li>
                        <p className="text-white text-sm">
                          Unlimited <span className="workspace-tooltip">workspaces</span>
                        </p>
                      </li>
                      {PRICING_CARD_BUSINESS_FEATURES.map((label) => (
                        <li key={label}>
                          <p className="text-white text-sm">{label}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto pt-4">
                      <button
                        type="button"
                        disabled={paidCheckoutBusy}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, plan: 'business' }));
                          handleBusinessPlan();
                        }}
                        className={`relative z-10 block w-full px-14 py-4 text-center font-medium tracking-wide border-2 border-[#C6FE1E] bg-white text-neutral-900 hover:bg-neutral-100 focus:ring-4 focus:ring-[#C6FE1E] focus:ring-opacity-40 rounded-full transition duration-300 ${paidCheckoutBusy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {dodoRedirectPlan === 'business'
                          ? 'Opening checkout…'
                          : 'Continue with Business'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Feature Comparison Section */}
          <section className="py-24 overflow-hidden bg-white">
            <div className="container px-4 mx-auto">
              <div className="mb-16 md:max-w-2xl text-center mx-auto">
                <h2 className="font-bold mb-4 text-4xl lg:text-5xl text-gray-900 tracking-tight">All our features in detail</h2>
                <p className="text-lg text-gray-600">See what you get in each plan, side by side.</p>
              </div>

              <div className="max-w-7xl mx-auto px-6">
                {/* Plan Headers */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                  <div className="hidden md:block"></div>
                  <div className="text-center">
                    <h3 className="font-bold text-xl text-gray-900 mb-4">Starter</h3>
                    <button
                      type="button"
                      onClick={handleStartFreePlan}
                      disabled={formData.subscribers > 1000}
                      className={`w-full px-6 py-3 text-sm font-medium border-2 rounded-lg transition-colors ${
                        formData.subscribers > 1000
                          ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                          : 'border-gray-300 text-gray-900 hover:border-[#C6FE1E] hover:text-[#C6FE1E]'
                      }`}
                    >
                      {formData.subscribers > 1000 ? 'Not available' : 'Start your free plan'}
                    </button>
                  </div>
                  <div className="text-center">
                    <h3 className="font-bold text-xl text-gray-900 mb-4">Pro</h3>
                    <button
                      type="button"
                      disabled={paidCheckoutBusy}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, plan: 'pro' }));
                        handleProPlan();
                      }}
                      className={`w-full px-6 py-3 text-sm font-medium border-2 border-gray-300 text-gray-900 rounded-lg hover:border-black transition-colors ${paidCheckoutBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {dodoRedirectPlan === 'pro' ? 'Opening checkout…' : 'Start now'}
                    </button>
                  </div>
                  <div className="text-center">
                    <h3 className="font-bold text-xl text-gray-900 mb-4">Business</h3>
                    <button
                      type="button"
                      disabled={paidCheckoutBusy}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, plan: 'business' }));
                        handleBusinessPlan();
                      }}
                      className={`w-full px-6 py-3 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors ${paidCheckoutBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {dodoRedirectPlan === 'business' ? 'Opening checkout…' : 'Start now'}
                    </button>
                  </div>
                </div>

                {/* Feature Rows */}
                <div className="space-y-0 border-t border-gray-200">
                  {planComparisonCategories.map((category) => {
                    const isExpanded = expandedFeatures[category.name] || false;
                    return (
                      <div key={category.name} className="border-b border-gray-200">
                        {/* Category Header */}
                        <div
                          className="grid grid-cols-1 md:grid-cols-4 gap-4 py-4 px-6 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => setExpandedFeatures(prev => ({ ...prev, [category.name]: !isExpanded }))}
                        >
                          <div className="flex items-center">
                            <span className="font-medium text-gray-900">{category.name}</span>
                          </div>
                          <div className="hidden md:block"></div>
                          <div className="hidden md:block"></div>
                          <div className="flex items-center justify-end">
                            <button>
                              <svg
                                className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        {/* Sub-features */}
                        {isExpanded && category.subFeatures.map((subFeature, idx) => (
                          <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-4 py-3 px-6 bg-gray-50 border-b border-gray-100 last:border-b-0">
                            <div className="flex items-center">
                              <span className="text-sm text-gray-700">{subFeature.name}</span>
                            </div>
                            <div className="flex items-center justify-center">
                              {subFeature.starter === '✓' ? (
                                <span className="text-sm text-gray-900">Yes</span>
                              ) : subFeature.starter === '—' ? (
                                <span className="text-gray-400">—</span>
                              ) : (
                                <span className="text-sm text-gray-900">{subFeature.starter}</span>
                              )}
                            </div>
                            <div className="flex items-center justify-center">
                              {subFeature.pro === '✓' ? (
                                <span className="text-sm text-gray-900">Yes</span>
                              ) : subFeature.pro === '—' ? (
                                <span className="text-gray-400">—</span>
                              ) : (
                                <span className="text-sm text-gray-900">{subFeature.pro}</span>
                              )}
                            </div>
                            <div className="flex items-center justify-center">
                              {subFeature.business === '✓' ? (
                                <span className="text-sm text-gray-900">Yes</span>
                              ) : subFeature.business === '—' ? (
                                <span className="text-gray-400">—</span>
                              ) : (
                                <span className="text-sm text-gray-900">{subFeature.business}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

    </>
    , document.body);
};

export default UpgradeModal;
